import * as React from 'react';

import { connect } from 'react-redux';
import withStyles from 'react-jss';
import { Dropdown, Icon, Menu, Tooltip } from 'antd';

import { StylesProps } from 'src/Library/Types';
import { ModesOfService } from 'src/Library/Constants';
import GetExecutionData from 'src/dto/response/getExecution';
import { rfqTypesNames } from 'src/utils/utils';
import DomesticTruckIcon from 'src/components/common/icons/DomesticTruckIcon';

import { MilestoneCodes, ProcurementType } from '../SE.constants';
import { getLegEndpointDisplay, getModeIcon, getTaskSummaryTotal } from '../SE.utils';
import styles from './SEShipmentSummaryBar.styles';

const ON_HOLD_MILESTONE_LABEL = 'On Hold';

export interface SEShipmentSummaryBarProps extends StylesProps<ReturnType<typeof styles>> {
  cardData: GetExecutionData;
  taskSummary?: Record<string, number>;
  showRoadInsteadOfDomestic?: boolean;
}

/**
 * Condensed header shown above the task/details panel while the shipment list is
 * collapsed, so the user can still tell which shipment execution they are on.
 * Mirrors the identifying details of SEShipmentCard.
 */
const SEShipmentSummaryBar: React.FC<SEShipmentSummaryBarProps> = ({
  classes,
  cardData,
  taskSummary,
  showRoadInsteadOfDomestic,
}) => {
  const isDomestic = cardData.mode === ModesOfService.DOMESTIC;
  const isPortBased = !isDomestic && cardData.mode !== ModesOfService.RPTL;

  const renderMode = () => (
    <div className={classes.mode}>
      {isDomestic ? (
        <DomesticTruckIcon
          strokeColor={'#000'}
          fill={'#000'}
          className={classes.modeIcon}
          aria-label={`${showRoadInsteadOfDomestic ? 'Road' : 'Domestic'} transport mode icon`}
        />
      ) : (
        <img
          src={getModeIcon(cardData.mode)}
          className={classes.modeIcon}
          alt={`${cardData.mode} transport mode icon`}
        />
      )}
      <span data-summary-mode={cardData.mode}>
        {isDomestic && showRoadInsteadOfDomestic ? 'Road' : cardData.mode}
      </span>
    </div>
  );

  const renderSpotBadge = () => {
    if (cardData.procurementType !== ProcurementType.SPOT) {
      return null;
    }
    return (
      <div className={classes.spotBadge} data-summary-procurement-type={cardData.procurementType}>
        Spot
      </div>
    );
  };

  const renderLeg = (endpoint: 'origin' | 'destination') => {
    const { title, code, name } = getLegEndpointDisplay(cardData, endpoint);
    return (
      <Tooltip title={title}>
        <div className={classes.leg}>
          <div className={classes.legCode} {...{ [`data-summary-${endpoint}-code`]: code || 'N/A' }}>
            {code || 'N/A'}
          </div>
          <div className={classes.legName} {...{ [`data-summary-${endpoint}-name`]: name || 'N/A' }}>
            {name || 'N/A'}
          </div>
        </div>
      </Tooltip>
    );
  };

  /**
   * Count of containers linked to the shipment; the numbers themselves are in
   * a dropdown, matching the equivalent control on the shipment card.
   */
  const renderContainerList = () => {
    const { containerList } = cardData;
    if (!containerList?.length) {
      return null;
    }
    const containerMenu = (
      <Menu>
        <Menu.Item className={classes.containerMenuHeader} key="containerNumberHeader" disabled>
          Container Number{containerList.length > 1 ? 's' : ''}
        </Menu.Item>
        {containerList.map((containerNumber: string) => (
          <Menu.Item className={classes.containerMenuItem} key={containerNumber}>
            <span data-summary-container-number={containerNumber}>{containerNumber}</span>
          </Menu.Item>
        ))}
      </Menu>
    );
    return (
      <Dropdown
        overlayClassName={classes.containerMenu}
        overlay={containerMenu}
        trigger={['click']}
        getPopupContainer={(trigger: any) => trigger.parentNode}
      >
        {/* Native button so Enter/Space raise the click the dropdown trigger listens for. */}
        <button
          type="button"
          className={classes.containerChip}
          data-summary-container-list={containerList.length}
        >
          <Icon type="inbox" />
          {containerList.length} Container{containerList.length > 1 ? 's' : ''}
        </button>
      </Dropdown>
    );
  };

  /** Status of the last integration call on the shipment (green ok, red failed). */
  const renderApiStatus = () => {
    const { extraData } = cardData;
    if (!extraData?.message) {
      return null;
    }
    return (
      <Tooltip title={extraData.apiResponseMessage || extraData.message}>
        <div
          className={`${classes.apiStatus} ${extraData.apiStatus ? classes.apiStatusSuccess : ''}`}
          data-summary-api-status={extraData.apiStatus ? 'success' : 'failure'}
        >
          <Icon type={extraData.apiStatus ? 'check-circle' : 'exclamation-circle'} />
          <span className={classes.apiStatusMessage} data-summary-api-message={`${cardData.id}_message`}>
            {extraData.message}
          </span>
        </div>
      </Tooltip>
    );
  };

  const renderMilestone = () => {
    const milestoneLabel = MilestoneCodes[cardData.milestone]?.label;
    if (!milestoneLabel) {
      return null;
    }
    const isOnHold = milestoneLabel === ON_HOLD_MILESTONE_LABEL;
    return (
      <Tooltip title="Current Milestone">
        <div
          className={`${classes.currentMilestone} ${isOnHold ? classes.currentMilestoneOnHold : ''}`}
          data-summary-milestone={milestoneLabel}
        >
          <Icon type={isOnHold ? 'pause-circle' : 'flag'} />
          {milestoneLabel}
        </div>
      </Tooltip>
    );
  };

  const renderTasks = () => {
    const totalTask = getTaskSummaryTotal(taskSummary);
    return (
      <Tooltip title="Tasks Completed">
        <div className={classes.tasks}>
          <Icon type="carry-out" theme="twoTone" />
          <div>
            <span className={classes.tasksCompleted} data-summary-completed-task={taskSummary?.Done || '0'}>
              {taskSummary?.Done || '0'}
            </span>
            <span data-summary-total-task={totalTask}>/{totalTask}</span>
          </div>
        </div>
      </Tooltip>
    );
  };

  return (
    <div className={classes.summaryBar} data-summary-shipment-id={cardData.id}>
      <div className={classes.identity}>
        <Tooltip title={cardData.masterReferenceNumber}>
          <div
            className={classes.referenceNumber}
            data-summary-reference-number={cardData.masterReferenceNumber}
          >
            {cardData.masterReferenceNumber}
          </div>
        </Tooltip>
        <div className={classes.divider}></div>
        <div className={classes.meta}>
          {renderMode()}
          <div className={classes.separatorDot}></div>
          <span data-summary-type={cardData.type}>{rfqTypesNames[cardData.type]}</span>
          {isPortBased && cardData.incoterm && (
            <>
              <div className={classes.separatorDot}></div>
              <span data-summary-incoterm={cardData.incoterm}>{cardData.incoterm}</span>
            </>
          )}
          {renderSpotBadge()}
        </div>
      </div>

      <div className={classes.divider}></div>

      <div className={classes.route}>
        {renderLeg('origin')}
        <Icon type="arrow-right" className={classes.routeArrow} />
        {renderLeg('destination')}
      </div>

      {cardData.carrierLogoUrl && (
        <img
          className={classes.carrierLogo}
          src={cardData.carrierLogoUrl}
          data-summary-logo={`${cardData.id}_logo`}
          alt="Carrier logo"
        />
      )}

      <div className={classes.status}>
        {renderContainerList()}
        {renderApiStatus()}
        {renderMilestone()}
        {renderTasks()}
      </div>
    </div>
  );
};

const mapStateToProps = ({ masterData }) => ({
  showRoadInsteadOfDomestic: masterData?.modules?.show_road_instead_of_domestic,
});

export default connect(mapStateToProps)(withStyles(styles)(React.memo(SEShipmentSummaryBar)));
